import type { ReactNode } from "react";
import { VIEWPORT_MD } from "~/constants";
import { useWindowDimensions } from "~/hooks/window-width";
import { Drawer } from "../Drawer/Drawer";
import { Modal } from "../Modal/Modal";
import { Stack } from "../Stack/Stack";
import type { Color } from "../utils/colors";

interface Props {
  open: boolean;
  title: string;
  onClose: () => void;
  background?: Color;
  // Rendered in the Modal's dedicated footer slot or appended inside the Drawer's body
  footer?: ReactNode;
  children: ReactNode;
}
/**
 * Renders a Modal on tablet/desktop and a Drawer on phones
 * The choice is made in JS (not CSS), so only one component is mounted at a time
 * and avoids issues with multiple hidden focus-lock and scroll-lock effects.
 */
export function ModalDrawer(props: Props): React.ReactNode {
  const { width } = useWindowDimensions();
  const isLargeScreen = width >= VIEWPORT_MD;

  if (isLargeScreen) {
    return (
      <Modal
        open={props.open}
        title={props.title}
        onClose={props.onClose}
        background={props.background}
        footer={props.footer}
      >
        {props.children}
      </Modal>
    );
  }

  return (
    <Drawer
      open={props.open}
      title={props.title}
      onClose={props.onClose}
      background={props.background}
    >
      {props.footer ? (
        <Stack space="4">
          {props.children}
          {props.footer}
        </Stack>
      ) : (
        props.children
      )}
    </Drawer>
  );
}
